import { createSlice } from '@reduxjs/toolkit'

import { postCreditCheckAPI, postCreditCheckDemoAPI, postTempOrder } from '@/api/shopApi'
import { SEC } from '@/util/formatters/dates'
import { setLocation } from '@/util/location'
import { appRoutes, sleep } from '@/util/misc'
import { getFromLocalStorage } from '@/util/persistance'
import { CREDIT_CHECK_TYPES, PAYMENT_TYPES, SHOP_STEPS } from '@/util/shop'

import {
  getCustomerContactEmail,
  getCustomerContactName,
  getCustomerContactPhone,
} from './customerReducer'
import { getProductFromId, reset as resetProducts } from './shopProductsReducer'
import {
  NAME as shopProgress,
  removeSteps,
  reset as resetProgress,
  setShopOrderProgressState,
} from './shopProgressReducer'

export const NAME = 'shop-order'
// @ts-ignore
export const createNewOrder = quoteName => async dispatch => {
  // todo: replace with a hook when refactored
  const whiteLabel = getFromLocalStorage('org')
  const isBechtle = ['bechtle', 'arp'].includes(whiteLabel)

  await dispatch(reset())
  await dispatch(resetProgress())
  await dispatch(setTempOrderName(quoteName))

  if (isBechtle) {
    dispatch(setPaymentType(PAYMENT_TYPES.LEASE))
    dispatch(removeSteps([SHOP_STEPS.ACCESSORIES]))
    dispatch(removeSteps([SHOP_STEPS.CONTRACT]))
    dispatch(removeSteps([SHOP_STEPS.CONFIRM]))
  }
}
// @ts-ignore
export const saveTempOrder = () => async (dispatch, getState) => {
  const state = getState()

  const tempOrderId = getTempOrderId(state)
  const customerId = getCustomerId(state)

  const isLeasing = getIsLeasing(state)
  const { buying_sum, leasing_sum } = getOrderTotalCost(state)

  const { [shopProgress]: shopProgressState, [NAME]: shopOrderState } = state

  const payload = {
    temp_order_id: tempOrderId,
    order_value: isLeasing ? leasing_sum : buying_sum,
    progress: shopProgressState,
    order: shopOrderState,
  }

  const newTempOrderId = await postTempOrder({ customerId, payload })
  dispatch(setTempOrderId(newTempOrderId))
  setLocation(`#${appRoutes.shopQuote.replace(':order_id', String(newTempOrderId))}`)
}
// @ts-ignore
export const postCreditCheck = () => async (dispatch, getState) => {
  const orderId = getTempOrderId(getState())

  dispatch(setHasCreditCheckError(false))
  try {
    await dispatch(saveTempOrder())
    // @ts-ignore
    const { status } = await postCreditCheckAPI(orderId)

    dispatch(setCreditCheckStatus(status))
  } catch {
    dispatch(setHasCreditCheckError(true))
    return false
  }
}
// @ts-ignore
export const postCreditCheckDemo = () => async (dispatch, getState) => {
  const orderId = getTempOrderId(getState())

  dispatch(setHasCreditCheckError(false))

  try {
    await sleep(5 * SEC)
    await dispatch(saveTempOrder())
    // @ts-ignore
    const { status, documentId } = await postCreditCheckDemoAPI(orderId)

    dispatch(setCreditCheckStatus(status))
    dispatch(setCreditCheckDocumentId(documentId))
  } catch {
    dispatch(setHasCreditCheckError(true))
    return false
  }
}

// @ts-ignore
// eslint-disable-next-line max-params
export const setShopState = (orderState, progressState, orderId, createdDate) => dispatch => {
  dispatch(resetProducts())
  dispatch(setShopOrderState(orderState))
  dispatch(setCreatedDate(createdDate))
  dispatch(setShopOrderProgressState(progressState))
  dispatch(setTempOrderId(orderId))
}
// @ts-ignore
export const createProductSelection = ({ productId, amount = 1 }) => ({
  productId,
  amount,
})

const initialState = {
  // todo: refactor to support different locale
  createdDate: new Date().toISOString(),

  paymentType: undefined,
  customerId: undefined,

  creditCheckStatus: undefined,
  creditCheckDocumentId: undefined,
  hasCreditCheckError: false,

  tempOrderId: undefined,
  ecomOrderId: undefined,
  tempOrderName: undefined,
  isCompleted: false,

  contactPersonName: undefined,
  contactPersonPhone: undefined,
  contactPersonEmail: undefined,

  selectedAddressId: undefined,

  selectedProducts: [],
}

const { actions, reducer } = createSlice({
  name: NAME,
  initialState,
  reducers: {
    setPaymentType: (state, { payload: paymentType }) => {
      state.paymentType = paymentType
      state.creditCheckStatus = undefined
    },
    setCustomerId: (state, { payload: customerId }) => {
      state.customerId = customerId
      state.creditCheckStatus = undefined
    },
    addProduct: (state, { payload }) => {
      const { productId } = payload
      const amount = correctAmount(payload.amount)
      const isProductInSelection = state.selectedProducts.some(
        // @ts-ignore
        product => product.productId === productId
      )
      state.creditCheckStatus = undefined
      if (isProductInSelection) {
        // @ts-ignore
        state.selectedProducts = state.selectedProducts.map(product =>
          // @ts-ignore
          product.productId === productId ? createProductSelection({ productId, amount }) : product
        )
      } else {
        state.selectedProducts = [
          // @ts-ignore
          ...state.selectedProducts,
          // @ts-ignore
          createProductSelection({ productId, amount }),
        ]
      }
    },
    removeProduct: (state, { payload: productId }) => {
      state.selectedProducts = state.selectedProducts.filter(
        // @ts-ignore
        selection => selection.productId !== productId
      )
      state.creditCheckStatus = undefined
    },
    resetSelectedProducts: state => {
      state.selectedProducts = initialState.selectedProducts
      state.creditCheckStatus = undefined
    },
    setCreditCheckStatus: (state, { payload: status }) => {
      state.creditCheckStatus = status
    },
    setCreditCheckDocumentId: (state, { payload: documentId }) => {
      state.creditCheckDocumentId = documentId
    },
    setHasCreditCheckError: (state, { payload: hasError }) => {
      state.hasCreditCheckError = hasError
    },
    setTempOrderId: (state, { payload: tempOrderId }) => {
      state.tempOrderId = tempOrderId
    },
    setTempOrderName: (state, { payload: tempOrderName }) => {
      state.tempOrderName = tempOrderName
    },
    setCreatedDate: (state, { payload: createdDate }) => {
      state.createdDate = createdDate
    },
    setContactPersonName: (state, { payload: contactPersonName }) => {
      state.contactPersonName = contactPersonName
    },
    setContactPersonPhone: (state, { payload: contactPersonPhone }) => {
      state.contactPersonPhone = contactPersonPhone
    },
    setContactPersonEmail: (state, { payload: contactPersonEmail }) => {
      state.contactPersonEmail = contactPersonEmail
    },
    setSelectedAddressId: (state, { payload: selectedAddressId }) => {
      state.selectedAddressId = selectedAddressId
    },
    setIsCompleted: (state, { payload: ecomOrderId }) => {
      state.isCompleted = true
      state.ecomOrderId = ecomOrderId
    },
    setShopOrderState: (state, { payload: newState }) => ({
      ...newState,
    }),
    reset: () => ({
      ...initialState,
      // todo: refactor to support different locale
      createdDate: new Date().toISOString(),
    }),
  },
})

export const {
  setPaymentType,
  setCustomerId,
  addProduct,
  removeProduct,
  resetSelectedProducts,
  setCreditCheckStatus,
  setCreditCheckDocumentId,
  setHasCreditCheckError,
  setTempOrderId,
  setCreatedDate,
  setTempOrderName,
  setContactPersonName,
  setContactPersonPhone,
  setContactPersonEmail,
  setSelectedAddressId,
  setIsCompleted,
  setShopOrderState,
  reset,
} = actions

export default reducer

const getState = (state: any) => state[NAME]

export const getTempOrderId = (state: any) => getState(state).tempOrderId
export const getTempOrderName = (state: any) => getState(state).tempOrderName
export const getIsCompleted = (state: any) => getState(state).isCompleted

export const getPaymentType = (state: any) => getState(state).paymentType
export const getIsPaymentTypeSet = (state: any) => getPaymentType(state) !== undefined
export const getIsLeasing = (state: any) => getPaymentType(state) === PAYMENT_TYPES.LEASE

export const getIsCreditCheckStatus = (state: any) => getState(state).creditCheckStatus
export const getIsCreditCheckPerformed = (state: any) =>
  getIsCreditCheckStatus(state) !== initialState.creditCheckStatus
export const getIsCreditCheckGranted = (state: any) =>
  getState(state).creditCheckStatus === CREDIT_CHECK_TYPES.APPROVED
export const getCreditCheckDocumentId = (state: any) => getState(state).creditCheckDocumentId
export const getHasCreditCheckError = (state: any) => getState(state).hasCreditCheckError

export const getContactPersonName = (state: any) =>
  getState(state).contactPersonName || getCustomerContactName(state)
export const getContactPersonPhone = (state: any) =>
  getState(state).contactPersonPhone || getCustomerContactPhone(state)
export const getContactPersonEmail = (state: any) =>
  getState(state).contactPersonEmail || getCustomerContactEmail(state)

export const getSelectedAddressId = (state: any) => getState(state).selectedAddressId

export const getCustomerId = (state: any) => getState(state).customerId
export const getSelectedProducts = (state: any) => getState(state).selectedProducts
// @ts-ignore
export const getIsProductSelected = (state, productId) =>
  // @ts-ignore
  getSelectedProducts(state).some(selection => selection.productId === productId)

export const getSelectedProductsMapped = (state: any) =>
  getSelectedProducts(state)
    // @ts-ignore
    .map(product => {
      const productFromId = getProductFromId(state, product.productId)
      return {
        amount: product.amount,
        ...productFromId,
      }
    })
    // @ts-ignore
    .filter(product => !!product.name)
// @ts-ignore
export const getOrderTotalCost = state =>
  getSelectedProductsMapped(state).reduce(
    // @ts-ignore
    (acc, cur) => {
      const monthlyLeasingPrice = (cur.leasing_price * cur.amount) / cur.leasing_duration_months
      return {
        buying_sum: acc.buying_sum + cur.buying_price * cur.amount,
        leasing_sum: acc.leasing_sum + cur.leasing_price * cur.amount,
        leasing_monthly: acc.leasing_monthly + monthlyLeasingPrice,
        leasing_duration_months: Math.max(acc.leasing_duration_months, cur.leasing_duration_months),
        leasing_durations: {
          ...acc.leasing_durations,
          [cur.leasing_duration_months]: acc.leasing_durations[cur.leasing_duration_months]
            ? acc.leasing_durations[cur.leasing_duration_months] + monthlyLeasingPrice
            : monthlyLeasingPrice,
        },
      }
    },
    {
      buying_sum: 0,
      leasing_sum: 0,
      leasing_monthly: 0,
      leasing_duration_months: 0,
      leasing_durations: {},
    }
  )
// @ts-ignore
const correctAmount = amount => (amount < 0 || isNaN(amount) ? 0 : amount)
